import React, { FunctionComponent } from 'react';
import cx from 'classnames';
import Touchable, { TouchableProps } from '../Touchable';

import styles from './IconButton.module.scss';
import Tooltip from '../Tooltip';

export interface IconButtonProps extends TouchableProps {
  /** accessibilityLabel should provide meaningful context about button to those who rely on a screen reader to interact with the UI */
  accessibilityLabel: string;
  /** Optional custom text for the IconButton's tooltip. If not provided, the accessibilityLabel is used. */
  tooltipText?: string;
  /** If you are applying positioning styles the IconButton directly rather than via an ancestor, use this classname prop to set those positioning attributes, e.g. top, left, right, margin, etc. */
  wrapperClassName?: string;
  /** Apply a className to change the icon color. */
  iconClassName?: string;
  /** The icon to display inside the IconButton */
  icon: React.ReactElement;
}

export type Props = IconButtonProps;

export const IconButton: FunctionComponent<IconButtonProps> = ({
  accessibilityLabel,
  tooltipText,
  className,
  wrapperClassName,
  iconClassName,
  icon,
  ...restProps
}) => {
  const isTouchDevice: boolean =
    typeof matchMedia === 'function'
      ? matchMedia('(hover: none)').matches
      : false;

  return (
    <Tooltip
      dark
      placement='top'
      showOn={isTouchDevice ? 'disabled' : ['hover', 'focus']}
      small
      target={
        <Touchable
          ariaLabel={accessibilityLabel}
          className={cx(styles.container, className)}
          {...restProps}
        >
          {React.cloneElement(icon, {
            className: cx(styles.icon, iconClassName),
          })}
        </Touchable>
      }
      wrapperClassName={wrapperClassName}
    >
      {tooltipText || accessibilityLabel}
    </Tooltip>
  );
};

IconButton.displayName = 'IconButton';
export default IconButton;
export { styles };
